import { ASSESSMENT_CYCLE_YEAR, BTI_WAVE1_EVALUATION, longDate } from '@grc/shared/vocabulary';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useApi, type Loadable } from './api';
import { useAuth } from './auth';
import { config } from './config';
import { ThemeToggle } from './theme';
import type { Me } from './types';
import { FormerFirmPage } from './pages/FormerFirm';
import { DashboardTour, itLeadFirm } from './walkthrough';

/** Production only (issue 33): each source with its own date, under the top tabs. Dev and staging keep the header they have. */
const DATA_AS_OF = `CDW ${ASSESSMENT_CYCLE_YEAR} assessment, ${ASSESSMENT_CYCLE_YEAR}; March 2025 survey, March 2025; BTI review, ${longDate(BTI_WAVE1_EVALUATION.submittedOn)}; firms' own plans and ratings, as each firm reports them`;

export function useMe() {
  return useApi<Me>('/me');
}

/** The layout's read of /me, passed to every page under it, so a page does not read it a second time (CP-07). */
export function useMeContext() {
  return useOutletContext<Loadable<Me>>();
}

export function Layout() {
  const { signOut } = useAuth();
  const { pathname } = useLocation();
  const me = useMe();
  const leadFirm = me.data ? itLeadFirm(me.data, pathname) : null;
  // The button stays on firm pages. Remember the firm during render so the tour stays mounted on Home and can come back.
  const [tourFirm, setTourFirm] = useState<string | null>(null);
  if (leadFirm && leadFirm !== tourFirm) setTourFirm(leadFirm);
  const firms = me.data?.firms ?? [];
  const network = me.data && (me.data.tiers.national || me.data.tiers.board);
  const formerOnly = Boolean(me.data && (me.data.formerFirms ?? []).length > 0 && firms.length === 0 && !network);
  // The National view is National IT's page (DEC-22, DEC-33); the Board reads the network on Leadership.
  const nationalIt = Boolean(me.data?.tiers.national);
  return (
    <>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <header className="top">
        <div className="shell top-bar">
          <div className="brand">
            <img src="/logo-white.svg" alt="Baker Tilly" />
            <span className="title">Cyber Resilience Program</span>
          </div>
          <div className="top-actions">
            {tourFirm && <DashboardTour firmCode={tourFirm} showButton={Boolean(leadFirm)} />}
            <ThemeToggle />
            <button type="button" className="header-text" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
        <div className="top-nav">
          <nav aria-label="Main" className="nav shell">
            <NavLink to="/" end>
              Home
            </NavLink>
            <NavLink to="/guidance">Guidance</NavLink>
            {(firms.length > 0 || network) && <NavLink to="/leadership">Leadership</NavLink>}
            {firms.map((f) => (
              <NavLink key={f.firmCode} to={`/firm/${f.firmCode}`}>
                {f.displayName}
              </NavLink>
            ))}
            {nationalIt && <NavLink to="/national">National view</NavLink>}
            {/* National IT reviewers have the queue as the National view's Reviews tab (5D B); other reviewers keep this link. */}
            {me.data?.tiers.reviewer && !nationalIt && <NavLink to="/review">Review</NavLink>}
          </nav>
        </div>
        {config.environment === 'prod' && (
          <div className="data-as-of">
            <p>
              <span className="data-as-of-label">Data as of </span>
              {DATA_AS_OF}.
            </p>
            <p>Figures reflect current member firms only.</p>
          </div>
        )}
      </header>
      <main id="main" className="shell" tabIndex={-1}>
        {formerOnly ? <FormerFirmPage /> : <Outlet context={me} />}
      </main>
      <footer className="shell foot">
        <p>
          {config.environment !== 'prod' && <strong>{config.environment} environment, demonstration data only. </strong>}
          National IT runs this portal; each firm owns its information and decisions.{' '}
          <a href="/help/data-terms">How your information is handled</a>
        </p>
      </footer>
    </>
  );
}
